// Das Übungsufer (K46, Kampf-Designer mit Fabi, 25./26.10.2026): immer derselbe Kampf, an dem man Pause, Befehle und
// Kniffe ausprobiert. Vier Freunde auf Level 1 gegen sechs Sturmwesen wie an Tag 1, feste Hindernisse, fester Startwert.
// Der Run bleibt, wie er ist; die neuen Regeln hängen an Schaltern der Aufstellung (commandMode und Co.).
import { combatRules, friendStats, getAbility, getSpecies, practice } from '../content/index.ts';
import type { Battle } from './battle/core.ts';
import { EPS, dist, gap } from './battle/core.ts';
import type { BattleCommand, BattleSetup, CombatProfile, Unit } from './types.ts';

export type PracticePlacement = { x: number; y: number };

/** Harness und Tests: eine durchdachte Aufstellung für die Kombo-Folge (der Eisvogel hinter dem oberen Stein, der Waschbär dahinter) */
export const COMBO_PLACEMENT: PracticePlacement[] = [
  { x: 60, y: 46 },
  { x: 58, y: 34 },
  { x: 52, y: 26 },
  { x: 68, y: 31 },
];

/** Die Aufstellung des Übungsufers; placements: wo die vier Freunde stehen (sonst wie in practice.json) */
export function practiceSetup(placements?: PracticePlacement[], opts: { autoKniffe?: boolean } = {}): BattleSetup {
  return {
    friends: practice.friends.map((f, i) => {
      const sp = getSpecies(f.species);
      const entry = sp.classes.find((c) => c.friendClass === f.friendClass)!;
      const base = friendStats(f.species, f.friendClass);
      // Level 1, ohne Äste und Items; das Tempo der Spezies wie im Run (K28)
      const profile: CombatProfile = {
        stats: { ...base, moveSpeed: base.moveSpeed * (sp.speed ?? 1) },
        kniff: f.kniff,
        ...(f.passive ? { passive: f.passive } : {}),
        ...(entry.attackHeal ? { attackHeal: entry.attackHeal } : {}),
        perks: { mutGain: f.mutGain, startMut: f.startMut },
      };
      const p = placements?.[i] ?? f;
      return { id: f.species, species: f.species, friendClass: f.friendClass, kniff: undefined, name: f.name, profile, x: p.x, y: p.y };
    }),
    enemies: practice.enemies.map((e) => ({ enemy: e.enemy, x: e.x, y: e.y, ...(e.mutGain !== undefined ? { mutGain: e.mutGain } : {}), ...(e.kniff ? { kniff: e.kniff } : {}) })),
    stormLevel: practice.stormLevel,
    fieldSlots: Math.max(combatRules.fieldSlots.min, practice.friends.length),
    retreat: 'letzter',
    calm: true,
    commandMode: true,
    ...(opts.autoKniffe ? { autoKniffe: true } : {}),
    projectiles: true,
    telegraphs: true,
    obstacles: practice.obstacles.map(({ id, kind, x, y, radius, x2, y2 }) => ({ id, kind, x, y, radius, ...(x2 !== undefined ? { x2, y2 } : {}) })),
    hpFactor: practice.hpFactor,
  };
}

/** Welche Art Ziel ein Kniff braucht: einen Ort, einen Freund oder ein Sturmwesen (für Befehle und Vorschau) */
export function kniffAim(kniff: string): 'place' | 'friend' | 'enemy' | 'none' {
  switch (getAbility(kniff).mechanic) {
    case 'mulde':
    case 'ball':
      return 'place';
    case 'beckon':
      return 'friend';
    case 'weakspot':
    case 'strike':
      return 'enemy';
    default:
      return 'none';
  }
}

const ready = (u: Unit) => u.alive && !!u.kniff && u.mut >= u.mutMax - EPS && !u.travel && !u.spin && !u.swoop && u.digUntil <= 0 && u.order?.kind !== 'kniff';
const foesOf = (b: Battle) => b.units.filter((o) => o.side === 'enemy' && o.alive);

/**
 * Harness und Tests: eine feste Kombo-Folge, wie ein Spieler sie spielen würde. Der Eisvogel zeichnet das Sturmwesen
 * mit dem meisten Leben, der Dachs zieht dort alle zusammen, der Igel rollt hinein und trifft den Gezeichneten doppelt,
 * der Eisvogel stürzt hinterher, und der Waschbär holt den Igel aus dem Pulk (oder wer es gerade braucht).
 * Gibt die Befehle zurück, die ab dem nächsten Takt gelten
 */
export function comboPolicy(b: Battle): BattleCommand[] {
  const tick = Math.round(b.t / b.rules.tickSeconds) + 1;
  const out: BattleCommand[] = [];
  const byKniff = (m: string) => b.units.find((u) => u.side === 'friend' && u.alive && u.kniff && getAbility(u.kniff).mechanic === m);
  const foes = foesOf(b);
  if (!foes.length) return out;
  const bird = byKniff('weakspot');
  const badger = byKniff('mulde');
  const hog = byKniff('ball');
  const coon = byKniff('beckon');
  const marked = foes.find((o) => o.marked && o.markHold && !o.markClaim);
  // 1. Zeichnen: wer am meisten Leben hat
  if (bird && ready(bird) && !marked) {
    const big = foes.reduce((a, o) => (o.hp > a.hp ? o : a));
    out.push({ tick, unit: bird.uid, kind: 'kniff', target: big.uid });
  }
  const soon = (u: Unit | undefined) => !!u && u.alive && u.mut >= u.mutMax * 0.8;
  // 2. Zusammenziehen: dort, wo die meisten in den Umkreis fallen, am liebsten mit dem Gezeichneten; wartet kurz auf das Zeichen
  if (badger && ready(badger) && (marked || !soon(bird))) {
    const k = badger.kniff!;
    const reach = getAbility(k).params.reach!;
    const radius = getAbility(k).params.radius!;
    let best: { x: number; y: number; n: number } | null = null;
    for (const c of foes) {
      const d = dist(badger, c);
      const s = d > reach ? reach / d : 1;
      const p = { x: badger.x + (c.x - badger.x) * s, y: badger.y + (c.y - badger.y) * s };
      const n = foes.filter((o) => Math.hypot(o.x - p.x, o.y - p.y) - o.radius - badger.radius <= radius).length + (marked && Math.hypot(marked.x - p.x, marked.y - p.y) - marked.radius - badger.radius <= radius ? 2 : 0);
      if (!best || n > best.n) best = { ...p, n };
    }
    if (best && best.n >= 3) out.push({ tick, unit: badger.uid, kind: 'kniff', x: best.x, y: best.y });
  }
  // 3. Hineinrollen, sobald sie festhängen; kommt der Dachs nicht bald, gleich auf den Gezeichneten oder ins dichteste Gedränge
  const stuck = foes.filter((o) => o.stuckUntil > b.t + 0.8);
  const badgerBusy = !!badger && (badger.travel !== null || badger.digUntil > 0);
  if (hog && ready(hog) && (stuck.length >= 2 || (!badgerBusy && !soon(badger)))) {
    const radius = getAbility(hog.kniff!).params.radius!;
    const crowd = (c: Unit) => foes.filter((o) => dist(o, c) - o.radius - hog.radius <= radius).length + (c === marked ? 1 : 0);
    const pool = stuck.length >= 2 ? stuck : foes;
    const at = marked && pool.includes(marked) && crowd(marked) >= 2 ? marked : pool.reduce((a, o) => (crowd(o) > crowd(a) ? o : a), pool[0]!);
    if (at && (stuck.length >= 2 || crowd(at) >= 2)) out.push({ tick, unit: hog.uid, kind: 'kniff', x: at.x, y: at.y });
  }
  // 5. Ausweichen (K48): Wer im Streifen eines angesagten Rammstoßes steht, tritt zur Seite; springt der Windschnitt
  // nach hinten, nehmen ihn Igel und Dachs in die Mangel
  for (const e of foes) {
    const w = e.windup;
    if (w && w.shape.kind === 'stripe' && w.at - b.t < 1.3) {
      const sh = w.shape;
      for (const f of b.units) {
        if (f.side !== 'friend' || !f.alive || f.kind === 'tree' || f.travel || f.spin || f.swoop || f.order?.kind === 'move') continue;
        const rx = f.x - sh.x;
        const ry = f.y - sh.y;
        const along = rx * sh.dx + ry * sh.dy;
        const across = rx * sh.dy - ry * sh.dx;
        if (along < -f.radius || along > sh.length + f.radius || Math.abs(across) > sh.width / 2 + f.radius) continue;
        const side = across >= 0 ? 1 : -1;
        const out2 = sh.width / 2 + f.radius + 2 - Math.abs(across);
        out.push({ tick, unit: f.uid, kind: 'move', x: f.x + sh.dy * side * out2, y: f.y - sh.dx * side * out2 });
      }
    }
    // Springt der Windschnitt nach hinten, holt ihn sich der flinke Igel; der Dachs bleibt vorn
    if (e.raid && hog && hog.alive && !hog.travel && !hog.spin && !hog.order && dist(hog, e) < 40) out.push({ tick, unit: hog.uid, kind: 'attack', target: e.uid });
  }
  // 4. Herbeiwinken: wer anteilig am meisten verloren hat, der Igel nach dem Kreiseln zuerst
  if (coon && ready(coon)) {
    // Wer gleich selbst einen Kniff macht oder gerade kreiselt, bleibt, wo er ist
    const hurt = b.units.filter((o) => o.side === 'friend' && o.alive && o.kind !== 'tree' && o !== coon && o.hp < o.maxHp * 0.7 && !ready(o) && !o.spin && !o.travel);
    const pick = hurt.sort((a, c) => a.hp / a.maxHp - c.hp / c.maxHp)[0];
    if (pick && gap(coon, pick) > 3) out.push({ tick, unit: coon.uid, kind: 'kniff', target: pick.uid });
  }
  return out;
}
